import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, formatDate } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { ShieldCheck, Clock, Flag, ScrollText, MessageSquare, AlertTriangle } from 'lucide-react';

const CATS = ['licensing', 'careers', 'supervision', 'internships', 'documentation', 'private-practice', 'medical-social-work', 'school-social-work'];
const RISK_CLS = { high: 'bg-red-100 text-red-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-gray-100 text-gray-500' };

const AdminCommunity = () => {
  const navigate = useNavigate();
  const { isAdmin, loading: authLoading } = useAuth();
  const [summary, setSummary] = useState({});
  const [qQueue, setQQueue] = useState([]);
  const [aQueue, setAQueue] = useState([]);
  const [reports, setReports] = useState([]);
  const [audit, setAudit] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [bannedWords, setBannedWords] = useState([]);
  const [members, setMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [annForm, setAnnForm] = useState({ title: '', body: '' });
  const [newWord, setNewWord] = useState('');
  const [mod, setMod] = useState(null); // {kind, item}
  const [form, setForm] = useState({ note: '', edited_title: '', edited_body: '', category_slug: '' });

  useEffect(() => { document.title = 'Community Management | Admin'; }, []);
  useEffect(() => { if (!authLoading && !isAdmin) navigate('/'); }, [authLoading, isAdmin, navigate]);

  const loadAll = useCallback(async () => {
    try {
      const [s, q, a, r, au] = await Promise.all([
        axios.get(`${API_URL}/community/admin/summary`),
        axios.get(`${API_URL}/community/admin/queue?kind=questions&status=pending_review`),
        axios.get(`${API_URL}/community/admin/queue?kind=answers&status=pending_review`),
        axios.get(`${API_URL}/community/admin/reports`),
        axios.get(`${API_URL}/community/admin/audit`),
      ]);
      setSummary(s.data); setQQueue(q.data.items || []); setAQueue(a.data.items || []);
      setReports(r.data.reports || []); setAudit(au.data.audit || []);
      const [an, bw] = await Promise.all([
        axios.get(`${API_URL}/community/admin/announcements`),
        axios.get(`${API_URL}/community/admin/banned-words`),
      ]);
      setAnnouncements(an.data.announcements || []); setBannedWords(bw.data.words || []);
      const mem = await axios.get(`${API_URL}/community/admin/members`);
      setMembers(mem.data.members || []);
    } catch (e) { /* noop */ }
  }, []);
  useEffect(() => { if (isAdmin) loadAll(); }, [isAdmin, loadAll]);

  const openMod = (kind, item) => {
    setMod({ kind, item });
    setForm({ note: '', edited_title: item.title || '', edited_body: item.body || '', category_slug: item.category_slug || '' });
  };

  const doModerate = async (action) => {
    const { kind, item } = mod;
    const url = kind === 'question'
      ? `${API_URL}/community/admin/questions/${item.question_id}/moderate`
      : `${API_URL}/community/admin/answers/${item.answer_id}/moderate`;
    const payload = kind === 'question'
      ? { action, note: form.note, edited_title: form.edited_title !== item.title ? form.edited_title : undefined, edited_body: form.edited_body !== item.body ? form.edited_body : undefined, category_slug: form.category_slug !== item.category_slug ? form.category_slug : undefined }
      : { action, note: form.note, edited_body: form.edited_body !== item.body ? form.edited_body : undefined };
    try {
      const res = await axios.post(url, payload);
      toast.success(res.data.message);
      setMod(null); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed.'); }
  };

  const resolveReport = async (id) => {
    try { await axios.post(`${API_URL}/community/admin/reports/${id}/resolve`); toast.success('Report resolved.'); loadAll(); }
    catch (e) { toast.error('Failed.'); }
  };

  const postAnnouncement = async () => {
    if (!annForm.title.trim() || !annForm.body.trim()) { toast.error('Add a title and message.'); return; }
    try { await axios.post(`${API_URL}/community/admin/announcements`, { ...annForm, pinned: true }); toast.success('Announcement posted.'); setAnnForm({ title: '', body: '' }); loadAll(); }
    catch (e) { toast.error('Failed.'); }
  };
  const deleteAnnouncement = async (id) => {
    try { await axios.delete(`${API_URL}/community/admin/announcements/${id}`); toast.success('Removed.'); loadAll(); } catch (e) { toast.error('Failed.'); }
  };
  const addWord = async () => {
    if (!newWord.trim()) return;
    try { await axios.post(`${API_URL}/community/admin/banned-words`, { word: newWord }); toast.success('Word added.'); setNewWord(''); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed.'); }
  };
  const deleteWord = async (id) => {
    try { await axios.delete(`${API_URL}/community/admin/banned-words/${id}`); toast.success('Removed.'); loadAll(); } catch (e) { toast.error('Failed.'); }
  };

  const searchMembers = async () => {
    try { const r = await axios.get(`${API_URL}/community/admin/members`, { params: memberSearch ? { search: memberSearch } : {} }); setMembers(r.data.members || []); }
    catch (e) { toast.error('Search failed.'); }
  };
  const memberAction = async (userId, action, note = '', days = 0) => {
    try { const r = await axios.post(`${API_URL}/community/admin/members/${userId}/action`, { action, note, days }); toast.success(r.data.message); searchMembers(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Action failed.'); }
  };

  const Stat = ({ label, value, Icon, cls }) => (
    <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${cls}`}><Icon className="w-5 h-5" /></div>
      <div><p className="text-2xl font-bold text-primary leading-none">{value ?? 0}</p><p className="text-xs text-gray-500 mt-1">{label}</p></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F7F5EF] pb-16">
      <section className="bg-primary pt-24 pb-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <p className="inline-flex items-center gap-2 text-secondary font-semibold uppercase text-xs tracking-widest"><ShieldCheck className="w-4 h-4" /> Community Management</p>
          <h1 className="font-serif text-3xl font-bold mt-1">Moderation Queue</h1>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
          <Stat label="Questions pending" value={summary.questions_pending} Icon={Clock} cls="bg-amber-100 text-amber-700" />
          <Stat label="Answers pending" value={summary.answers_pending} Icon={MessageSquare} cls="bg-amber-100 text-amber-700" />
          <Stat label="Open reports" value={summary.open_reports} Icon={Flag} cls="bg-red-100 text-red-700" />
          <Stat label="Published" value={summary.published_total} Icon={ShieldCheck} cls="bg-emerald-100 text-emerald-700" />
          <Stat label="Unanswered" value={summary.unanswered} Icon={AlertTriangle} cls="bg-gray-100 text-gray-600" />
        </div>

        <Tabs defaultValue="questions">
          <TabsList className="bg-white border">
            <TabsTrigger value="questions" data-testid="tab-questions">Questions ({qQueue.length})</TabsTrigger>
            <TabsTrigger value="answers" data-testid="tab-answers">Answers ({aQueue.length})</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports">Reports ({reports.filter((r) => r.status === 'open').length})</TabsTrigger>
            <TabsTrigger value="announcements" data-testid="tab-announcements">Announcements</TabsTrigger>
            <TabsTrigger value="banned" data-testid="tab-banned">Banned Words</TabsTrigger>
            <TabsTrigger value="members" data-testid="tab-members">Members</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">Audit Log</TabsTrigger>
          </TabsList>

          <TabsContent value="questions" className="mt-4 space-y-3">
            {qQueue.length === 0 && <div className="bg-white rounded-2xl border p-8 text-center text-gray-500">No questions awaiting review.</div>}
            {qQueue.map((q) => (
              <div key={q.question_id} className="bg-white rounded-2xl border p-5" data-testid={`queue-q-${q.question_id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-secondary uppercase">{q.category}</span>
                  {q.risk && <span className={`text-xs px-2 py-0.5 rounded-full ${RISK_CLS[q.risk] || RISK_CLS.low}`}>{q.risk} risk</span>}
                  {(q.flags || []).map((f) => <span key={f} className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full">{f.replace(/_/g, ' ')}</span>)}
                </div>
                <h3 className="font-serif text-lg font-bold text-primary mt-1">{q.title}</h3>
                <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{q.body}</p>
                <p className="text-xs text-gray-400 mt-2">{q.author_display} · {formatDate(q.created_at)}</p>
                <div className="mt-3"><Button size="sm" onClick={() => openMod('question', q)} className="bg-secondary hover:bg-secondary-hover text-white" data-testid={`review-q-${q.question_id}`}>Review & Moderate</Button></div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="answers" className="mt-4 space-y-3">
            {aQueue.length === 0 && <div className="bg-white rounded-2xl border p-8 text-center text-gray-500">No answers awaiting review.</div>}
            {aQueue.map((a) => (
              <div key={a.answer_id} className="bg-white rounded-2xl border p-5" data-testid={`queue-a-${a.answer_id}`}>
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  {a.risk && <span className={`text-xs px-2 py-0.5 rounded-full ${RISK_CLS[a.risk] || RISK_CLS.low}`}>{a.risk} risk</span>}
                  {(a.flags || []).map((f) => <span key={f} className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full">{f.replace(/_/g, ' ')}</span>)}
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{a.body}</p>
                <p className="text-xs text-gray-400 mt-2">{a.author_display} · {formatDate(a.created_at)}</p>
                <div className="mt-3"><Button size="sm" onClick={() => openMod('answer', a)} className="bg-secondary hover:bg-secondary-hover text-white" data-testid={`review-a-${a.answer_id}`}>Review & Moderate</Button></div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="reports" className="mt-4 space-y-3">
            {reports.length === 0 && <div className="bg-white rounded-2xl border p-8 text-center text-gray-500">No reports.</div>}
            {reports.map((r) => (
              <div key={r.report_id} className="bg-white rounded-2xl border p-5 flex items-start justify-between gap-3" data-testid={`report-${r.report_id}`}>
                <div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === 'open' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>{r.status}</span>
                  <p className="text-sm text-gray-700 mt-1"><strong>{r.target_type}</strong>: {r.reason}</p>
                  <p className="text-xs text-gray-400 mt-1">Target ID: {r.target_id} · {formatDate(r.created_at)}</p>
                </div>
                {r.status === 'open' && <Button size="sm" variant="outline" onClick={() => resolveReport(r.report_id)} data-testid={`resolve-${r.report_id}`}>Resolve</Button>}
              </div>
            ))}
          </TabsContent>

          <TabsContent value="announcements" className="mt-4 space-y-3">
            <div className="bg-white rounded-2xl border p-5">
              <h3 className="font-semibold text-primary mb-2">Post an announcement</h3>
              <Input placeholder="Title" value={annForm.title} onChange={(e) => setAnnForm((f) => ({ ...f, title: e.target.value }))} data-testid="ann-title-input" />
              <Textarea rows={3} className="mt-2" placeholder="Message shown to all community members" value={annForm.body} onChange={(e) => setAnnForm((f) => ({ ...f, body: e.target.value }))} data-testid="ann-body-input" />
              <div className="flex justify-end mt-3"><Button onClick={postAnnouncement} className="bg-secondary hover:bg-secondary-hover text-white" data-testid="ann-post-btn">Post Announcement</Button></div>
            </div>
            {announcements.length === 0 && <div className="bg-white rounded-2xl border p-8 text-center text-gray-500">No announcements yet.</div>}
            {announcements.map((a) => (
              <div key={a.announcement_id} className="bg-white rounded-2xl border p-5 flex items-start justify-between gap-3" data-testid={`admin-ann-${a.announcement_id}`}>
                <div><p className="font-semibold text-primary">{a.title}</p><p className="text-sm text-gray-600 mt-1">{a.body}</p><p className="text-xs text-gray-400 mt-1">{formatDate(a.created_at)}</p></div>
                <Button size="sm" variant="outline" onClick={() => deleteAnnouncement(a.announcement_id)} className="text-red-600 border-red-200" data-testid={`del-ann-${a.announcement_id}`}>Delete</Button>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="banned" className="mt-4 space-y-3">
            <div className="bg-white rounded-2xl border p-5">
              <h3 className="font-semibold text-primary mb-1">Banned words &amp; phrases</h3>
              <p className="text-xs text-gray-500 mb-3">Questions or answers containing these terms are automatically held for moderator review.</p>
              <div className="flex gap-2">
                <Input placeholder="Add a word or phrase" value={newWord} onChange={(e) => setNewWord(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addWord()} data-testid="banned-word-input" />
                <Button onClick={addWord} className="bg-secondary hover:bg-secondary-hover text-white" data-testid="add-word-btn">Add</Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                {bannedWords.length === 0 && <p className="text-sm text-gray-400">No banned words yet.</p>}
                {bannedWords.map((w) => (
                  <span key={w.word_id} className="inline-flex items-center gap-1 bg-red-50 text-red-700 text-sm px-3 py-1 rounded-full" data-testid={`word-${w.word_id}`}>
                    {w.word}
                    <button onClick={() => deleteWord(w.word_id)} className="hover:text-red-900" data-testid={`del-word-${w.word_id}`}>×</button>
                  </span>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="members" className="mt-4 space-y-3">
            <div className="bg-white rounded-2xl border p-4 flex gap-2">
              <Input placeholder="Search members by name or email" value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && searchMembers()} data-testid="member-search-input" />
              <Button onClick={searchMembers} className="bg-secondary hover:bg-secondary-hover text-white" data-testid="member-search-btn">Search</Button>
            </div>
            {members.length === 0 && <div className="bg-white rounded-2xl border p-8 text-center text-gray-500">No community members yet. Search by name or email.</div>}
            {members.map((m) => {
              const st = m.community_status || 'active';
              const cls = st === 'suspended' ? 'bg-red-100 text-red-700' : st === 'muted' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700';
              return (
                <div key={m.user_id} className="bg-white rounded-2xl border p-5" data-testid={`member-${m.user_id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-primary">{m.name || 'Member'}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${cls}`}>{st}</span>
                      </div>
                      <p className="text-sm text-gray-500">{m.email}</p>
                      {m.community_mute_until && st === 'muted' && <p className="text-xs text-amber-600 mt-1">Muted until {m.community_mute_until.slice(0, 10)}</p>}
                      {(m.community_warnings || []).length > 0 && <p className="text-xs text-gray-400 mt-1">{m.community_warnings.length} warning(s) on record</p>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => memberAction(m.user_id, 'warn', 'Please review the community guidelines.')} data-testid={`warn-${m.user_id}`}>Warn</Button>
                      <Button size="sm" variant="outline" className="text-amber-700 border-amber-300" onClick={() => memberAction(m.user_id, 'mute', '', 7)} data-testid={`mute-${m.user_id}`}>Mute 7d</Button>
                      <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => memberAction(m.user_id, 'suspend')} data-testid={`suspend-${m.user_id}`}>Suspend</Button>
                      {st !== 'active' && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => memberAction(m.user_id, 'reactivate')} data-testid={`reactivate-${m.user_id}`}>Reactivate</Button>}
                    </div>
                  </div>
                </div>
              );
            })}
          </TabsContent>

          <TabsContent value="audit" className="mt-4">
            <div className="bg-white rounded-2xl border divide-y">
              {audit.length === 0 && <div className="p-8 text-center text-gray-500">No moderation activity yet.</div>}
              {audit.map((l) => (
                <div key={l.audit_id} className="p-4 flex items-center gap-3 text-sm">
                  <ScrollText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-gray-700"><strong>{l.actor_name}</strong> — {l.action.replace(/_/g, ' ')} <span className="text-gray-400">({l.target_type})</span></p>
                    {l.note && <p className="text-xs text-gray-500 mt-0.5">Note: {l.note}</p>}
                  </div>
                  <span className="text-xs text-gray-400">{formatDate(l.created_at)}</span>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Moderation dialog */}
      <Dialog open={!!mod} onOpenChange={(o) => !o && setMod(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Moderate {mod?.kind}</DialogTitle>
            <DialogDescription>Edit if needed, add a note, then choose an action. Members are notified by email on publish, revision, or rejection.</DialogDescription>
          </DialogHeader>
          {mod?.kind === 'question' && (
            <>
              <label className="text-sm font-medium">Title</label>
              <Input value={form.edited_title} onChange={(e) => setForm((f) => ({ ...f, edited_title: e.target.value }))} data-testid="mod-title-input" />
              <label className="text-sm font-medium mt-2">Category</label>
              <select className="w-full border rounded-md h-10 px-3 text-sm bg-white" value={form.category_slug} onChange={(e) => setForm((f) => ({ ...f, category_slug: e.target.value }))} data-testid="mod-category-select">
                {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </>
          )}
          <label className="text-sm font-medium mt-2">Body</label>
          <Textarea rows={6} value={form.edited_body} onChange={(e) => setForm((f) => ({ ...f, edited_body: e.target.value }))} data-testid="mod-body-input" />
          <label className="text-sm font-medium mt-2">Moderator note (shared with member on revision/rejection)</label>
          <Textarea rows={2} value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="Optional note" data-testid="mod-note-input" />
          <DialogFooter className="flex-wrap gap-2">
            {mod?.kind === 'question' ? (
              <>
                <Button onClick={() => doModerate('publish')} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="mod-publish-btn">Approve & Publish</Button>
                <Button onClick={() => doModerate('request_revision')} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="mod-revision-btn">Request Revision</Button>
                <Button onClick={() => doModerate('reject')} variant="outline" className="text-red-600 border-red-300" data-testid="mod-reject-btn">Reject</Button>
              </>
            ) : (
              <>
                <Button onClick={() => doModerate('publish')} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="mod-publish-btn">Approve & Publish</Button>
                <Button onClick={() => doModerate('reject')} variant="outline" className="text-red-600 border-red-300" data-testid="mod-reject-btn">Reject</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCommunity;
